import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ImagePlus, Loader2, Pin, Plus, Send, Trash2, X } from "lucide-react";
import { api, type BartAttachment, type BartMessage, type BartThread } from "@/lib/api";
import AnswerBody from "@/components/AnswerBody";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";
import { OP_RESULT_CARD } from "@/lib/opSurfaces";

function telegramAccount(thread: BartThread): string {
  return (thread.telegram_account || "").trim();
}

function threadTopic(thread: BartThread): string {
  const topic = (thread.topic || "").trim();
  if (topic) return topic;
  return (thread.title || "Nowa rozmowa").trim();
}

function layerLabel(layer: string): string {
  if (layer === "telegram-razem") return "RAZEM";
  if (layer === "transkrypcje") return "Spotkanie";
  if (layer === "sesje") return "Sesja";
  if (layer === "kanon") return "Kanon";
  if (layer === "youtube") return "YouTube";
  if (layer === "persona") return "Persona";
  return layer || "źródło";
}

type ChatItem = BartMessage & { thought?: BartMessage | null };

function pairMessages(messages: BartMessage[]): ChatItem[] {
  const out: ChatItem[] = [];
  let pending: BartMessage | null = null;
  for (const item of messages) {
    if (item.role === "thought") {
      pending = item;
      continue;
    }
    if (item.role === "assistant") {
      out.push({ ...item, thought: pending });
      pending = null;
      continue;
    }
    out.push(item);
  }
  return out;
}

function imageSrc(item: BartAttachment): string | null {
  if (item.url && item.url.startsWith("/")) return item.url;
  if (item.id) return `/api/bart/media/${item.id}`;
  return null;
}

function AttachmentStack({ items }: { items: BartAttachment[] }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-2">
      {items.map((att, idx) => {
        const key = `${att.kind}-${att.id || att.url || idx}`;
        if (att.kind === "image") {
          const src = imageSrc(att);
          if (!src) return null;
          return (
            <a
              key={key}
              href={src}
              target="_blank"
              rel="noreferrer"
              className="block overflow-hidden rounded-xl bg-background/60"
            >
              <img
                src={src}
                alt={att.title || "obraz"}
                className="max-h-72 w-full object-contain"
              />
              {att.title && (
                <p className="px-2 py-1.5 text-[11px] text-muted-foreground">{att.title}</p>
              )}
            </a>
          );
        }
        if (!att.url || !att.url.startsWith("http")) return null;
        return (
          <a
            key={key}
            href={att.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-2 rounded-xl bg-background/60 px-3 py-2 text-[13px] text-foreground hover:bg-background"
          >
            <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span>
              <span className="block font-medium">{att.title || att.url}</span>
              {att.label && (
                <span className="block text-[11px] text-muted-foreground">{att.label}</span>
              )}
            </span>
          </a>
        );
      })}
    </div>
  );
}

function ThoughtToggle({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-1.5 max-w-[85%]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-muted-foreground/80 underline-offset-2 hover:underline"
      >
        {open ? "ukryj myśli" : "myśli"}
      </button>
      {open && (
        <p className="mt-1 whitespace-pre-wrap rounded-xl bg-muted/40 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground">
          {text}
        </p>
      )}
    </div>
  );
}

function MessageBubble({ item }: { item: ChatItem }) {
  const mine = item.role === "user";
  const [showSources, setShowSources] = useState(false);
  const sources = item.sources ?? [];
  return (
    <div>
      {!mine && item.thought?.content && <ThoughtToggle text={item.thought.content} />}
      <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
        <div
          className={cn(
            "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
            mine
              ? "bg-brand text-brand-foreground"
              : "bg-accent/70 text-foreground",
          )}
        >
          {mine ? (
            <p className="whitespace-pre-wrap">{item.content}</p>
          ) : (
            <AnswerBody className="text-sm text-foreground" text={item.content} />
          )}
          <AttachmentStack items={item.attachments ?? []} />
          {!mine && sources.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowSources((v) => !v)}
                className="text-[11px] text-muted-foreground/80 underline-offset-2 hover:underline"
              >
                {showSources ? "ukryj skąd" : "skąd to"}
              </button>
              {showSources && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {sources.slice(0, 3).map((src) => (
                    <span
                      key={`${src.layer}-${src.source}`}
                      className="rounded-full bg-background/70 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {layerLabel(src.layer)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CONTINUOUS_ID = "bart-continuous";
const MAX_SHOTS = 3;
const IDLE_MS = 90_000;

type PendingShot = { key: string; file: File; url: string };

function addShotFromFile(file: File, current: PendingShot[]): PendingShot[] {
  if (!file.type.startsWith("image/")) return current;
  if (current.length >= MAX_SHOTS) return current;
  return [...current, { key: `${file.name}-${file.size}-${Date.now()}`, file, url: URL.createObjectURL(file) }];
}

function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  const seen = new Set<string>();
  const push = (file: File | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    const key = `${file.name}-${file.size}-${file.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(file);
  };
  for (const item of Array.from(data.items || [])) {
    if (item.kind === "file") push(item.getAsFile());
  }
  for (const file of Array.from(data.files || [])) {
    push(file);
  }
  return out;
}

export default function Bart() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string>("");
  const [draft, setDraft] = useState("");
  const [shots, setShots] = useState<PendingShot[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const lastActivityRef = useRef(Date.now());
  const idleStateRef = useRef({
    activeId: "",
    draft: "",
    shots: 0,
    chatBusy: false,
    nudgeBusy: false,
  });

  const bumpActivity = (extraMs = 0) => {
    lastActivityRef.current = Date.now() + extraMs;
  };

  const threadsQ = useQuery({
    queryKey: ["bart-threads"],
    queryFn: api.bartThreads,
  });
  const threads: BartThread[] = threadsQ.data?.items ?? [];

  useEffect(() => {
    if (!activeId && threads[0]?.id) setActiveId(threads[0].id);
  }, [activeId, threads]);

  const detailQ = useQuery({
    queryKey: ["bart-thread", activeId],
    queryFn: () => api.bartThread(activeId),
    enabled: Boolean(activeId),
  });
  const activeThread = threads.find((thread) => thread.id === activeId) ?? detailQ.data?.thread;
  const messages = detailQ.data?.messages ?? [];
  const chatItems = useMemo(() => pairMessages(messages), [messages]);
  const thoughts = useMemo(
    () => messages.filter((m) => m.role === "thought").slice(-12).reverse(),
    [messages],
  );

  const notesQ = useQuery({
    queryKey: ["bart-notes"],
    queryFn: api.bartNotes,
  });
  const notes = notesQ.data?.items ?? [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatItems.length]);

  const createM = useMutation({
    mutationFn: () => api.bartCreateThread("Nowa rozmowa"),
    onSuccess: (thread) => {
      qc.invalidateQueries({ queryKey: ["bart-threads"] });
      setActiveId(thread.id);
    },
  });

  const chatM = useMutation({
    mutationFn: async () => {
      const imageIds: string[] = [];
      for (const shot of shots) {
        const uploaded = await api.bartUploadImage(shot.file);
        imageIds.push(uploaded.id);
      }
      return api.bartChat(draft.trim(), activeId || undefined, imageIds);
    },
    onSuccess: (res) => {
      setDraft("");
      shots.forEach((shot) => URL.revokeObjectURL(shot.url));
      setShots([]);
      setActiveId(res.thread.id);
      qc.invalidateQueries({ queryKey: ["bart-threads"] });
      qc.invalidateQueries({ queryKey: ["bart-thread", res.thread.id] });
      qc.invalidateQueries({ queryKey: ["bart-notes"] });
    },
  });

  const pinM = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => api.bartPinThread(id, pinned),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bart-threads"] });
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.bartDeleteThread(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["bart-threads"] });
      qc.removeQueries({ queryKey: ["bart-thread", id] });
      if (activeId === id) setActiveId("");
    },
  });

  const nudgeM = useMutation({
    mutationFn: (threadId: string) => api.bartNudge(threadId),
    onSuccess: (res) => {
      const waitSec = Number(res.retry_after_sec || 0);
      bumpActivity(waitSec > 0 ? Math.max(0, waitSec * 1000 - IDLE_MS) : 0);
      if (res.skipped) return;
      setActiveId(res.thread.id);
      qc.invalidateQueries({ queryKey: ["bart-threads"] });
      qc.invalidateQueries({ queryKey: ["bart-thread", res.thread.id] });
      qc.invalidateQueries({ queryKey: ["bart-notes"] });
    },
    onError: () => {
      bumpActivity(120_000);
    },
  });

  const send = () => {
    if (chatM.isPending) return;
    if (!draft.trim() && shots.length === 0) return;
    chatM.mutate();
  };

  const takeFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    setShots((cur) => {
      let next = cur;
      for (const file of Array.from(files)) {
        next = addShotFromFile(file, next);
      }
      return next;
    });
  };

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = filesFromClipboard(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      takeFiles(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  useEffect(() => {
    idleStateRef.current = {
      activeId,
      draft,
      shots: shots.length,
      chatBusy: chatM.isPending,
      nudgeBusy: nudgeM.isPending,
    };
  }, [activeId, draft, shots.length, chatM.isPending, nudgeM.isPending]);

  useEffect(() => {
    bumpActivity();
  }, [activeId, draft, shots.length, chatItems.length]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") bumpActivity();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const nudgeMutateRef = useRef(nudgeM.mutate);
  nudgeMutateRef.current = nudgeM.mutate;

  useEffect(() => {
    const tick = () => {
      const state = idleStateRef.current;
      if (document.visibilityState !== "visible") return;
      if (!state.activeId || state.chatBusy || state.nudgeBusy) return;
      if (state.draft.trim() || state.shots > 0) return;
      if (Date.now() - lastActivityRef.current < IDLE_MS) return;
      bumpActivity();
      nudgeMutateRef.current(state.activeId);
    };
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, []);

  const removeShot = (key: string) => {
    setShots((cur) => {
      const hit = cur.find((item) => item.key === key);
      if (hit) URL.revokeObjectURL(hit.url);
      return cur.filter((item) => item.key !== key);
    });
  };

  return (
    <div>
      <PageHeader
        title="Bart"
        subtitle="Przy stole z Arkiem i Kasią. Pilnuje lejka, rytmu tygodnia i waszego balansu."
        actions={
          <Button variant="brand" onClick={() => createM.mutate()} disabled={createM.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Nowy wątek
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[260px_1fr_260px]">
        <Card className="h-[calc(100vh-12rem)] overflow-hidden">
          <CardContent className="h-full space-y-1 overflow-y-auto p-3">
            {threads.length === 0 && (
              <OpCallout variant="help" showLabel={false} className="mx-1">
                Brak wątków. Napisz pierwsze pytanie albo kliknij Nowy wątek.
              </OpCallout>
            )}
            {threads.map((thread) => {
              const pinned = Boolean(thread.pinned || thread.pinned_at);
              const continuous = thread.id === CONTINUOUS_ID || thread.channel === "continuous";
              const fromTelegram = thread.channel === "telegram";
              return (
                <div
                  key={thread.id}
                  className={cn(
                    "flex items-start gap-0.5",
                    activeId === thread.id
                      ? OP_RESULT_CARD.selected
                      : "rounded-lg text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setActiveId(thread.id)}
                    className="min-w-0 flex-1 rounded-lg px-3 py-2 text-left text-sm"
                  >
                    <div className="flex items-center gap-1.5">
                      {pinned && <Pin className="h-3 w-3 shrink-0 fill-current" aria-hidden />}
                      <span className="truncate font-medium">{threadTopic(thread)}</span>
                    </div>
                    <div className="truncate text-[11px] opacity-70">
                      {fromTelegram
                        ? `Telegram · ${telegramAccount(thread) || "konto Telegram"}`
                        : thread.updated_at}
                    </div>
                  </button>
                  <button
                    type="button"
                    aria-label={pinned ? "Odepnij wątek" : "Przypnij wątek"}
                    title={pinned ? "Odepnij" : "Przypnij"}
                    disabled={pinM.isPending}
                    onClick={() => pinM.mutate({ id: thread.id, pinned: !pinned })}
                    className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-background/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Pin className={cn("h-3.5 w-3.5", pinned && "fill-current text-foreground")} />
                  </button>
                  {!continuous && (
                    <button
                      type="button"
                      aria-label="Usuń wątek"
                      title="Usuń"
                      disabled={deleteM.isPending}
                      onClick={() => {
                        if (!window.confirm("Usunąć ten wątek?")) return;
                        deleteM.mutate(thread.id);
                      }}
                      className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-background/70 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="flex h-[calc(100vh-12rem)] flex-col overflow-hidden">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            {activeThread?.channel === "telegram" && (
              <div className="border-b border-border/50 px-5 py-2.5">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Telegram
                </p>
                <p className="text-sm text-foreground">
                  Konto: {telegramAccount(activeThread) || "konto Telegram"}
                </p>
              </div>
            )}
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
              {chatItems.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Piszcie normalnie albo wklejcie screen. Bart mówi do was obojga.
                </p>
              )}
              {chatItems.map((item) => (
                <MessageBubble key={item.id} item={item} />
              ))}
              {(chatM.isPending || nudgeM.isPending) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {nudgeM.isPending && !chatM.isPending ? "Bart zagląda…" : "Bart myśli…"}
                </div>
              )}
              {chatM.isError && (
                <p className="text-sm text-destructive">
                  Nie poszło. Sprawdź GEMINI_API_KEY albo spróbuj jeszcze raz.
                </p>
              )}
              <div ref={bottomRef} />
            </div>
            <div className="border-t border-border/50 p-3">
              {shots.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {shots.map((shot) => (
                    <div key={shot.key} className="relative">
                      <img
                        src={shot.url}
                        alt="screen do Barta"
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                      <button
                        type="button"
                        aria-label="Usuń screen"
                        onClick={() => removeShot(shot.key)}
                        className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background text-foreground shadow hover:bg-accent"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    takeFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Dodaj screen"
                  title="Dodaj screen albo wklej Ctrl+V"
                  disabled={chatM.isPending || shots.length >= MAX_SHOTS}
                  onClick={() => fileRef.current?.click()}
                >
                  <ImagePlus className="h-4 w-4" />
                </Button>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onDrop={(e) => {
                    const files = Array.from(e.dataTransfer?.files || []).filter((f) =>
                      f.type.startsWith("image/"),
                    );
                    if (!files.length) return;
                    e.preventDefault();
                    takeFiles(files);
                  }}
                  onDragOver={(e) => {
                    if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={2}
                  placeholder="Napiszcie albo wklejcie screen (Ctrl+V)…"
                  className="min-h-[44px] flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <Button
                  variant="brand"
                  size="icon"
                  onClick={send}
                  disabled={(!draft.trim() && shots.length === 0) || chatM.isPending}
                >
                  {chatM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hidden h-[calc(100vh-12rem)] overflow-hidden lg:block">
          <CardContent className="h-full overflow-y-auto p-4">
            <h2 className="text-sm font-medium">Rozmyślania</h2>
            <p className="mt-1 text-[11px] text-muted-foreground">
              To, czego nie mówi na głos. Telegram dostaje tylko wypowiedź.
            </p>
            <div className="mt-3 space-y-3">
              {thoughts.length === 0 && (
                <p className="text-[12px] text-muted-foreground">Jeszcze nic tu nie zapisał.</p>
              )}
              {thoughts.map((item) => (
                <p
                  key={item.id}
                  className="whitespace-pre-wrap rounded-xl bg-muted/40 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground"
                >
                  {item.content}
                </p>
              ))}
            </div>
            {notes.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium">Pamięta</h3>
                <ul className="mt-2 space-y-1.5 text-[12px] text-muted-foreground">
                  {notes.map((note) => (
                    <li key={note.id}>
                      <span className="text-foreground/70">{note.kind}</span>
                      {": "}
                      {note.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
